import { captureError } from "./utils/logger";

const hasSemanticMeaning = (el: Element, checkChildren: boolean = false, depth: number = 3): boolean => {
    const hasRole = el.hasAttribute('role');
    const hasId = el.hasAttribute('id');
    const hasTabIndex = el.hasAttribute('tabindex');
    const isSemanticTag = ['header', 'nav', 'main', 'section', 'article', 'aside', 'footer', 'form', 'body'].includes(el.tagName.toLowerCase());
    const hasDataComponent = el.hasAttribute('data-component-name');

    const elHasSemanticMeaning = hasRole || hasId || hasTabIndex || isSemanticTag || hasDataComponent;

    if (!elHasSemanticMeaning && checkChildren === true && depth > 0) {
        const children = Array.from(el.children);
        return children.some(child => hasSemanticMeaning(child, checkChildren, depth - 1));
    }

    return elHasSemanticMeaning;
};

const isLargeElement = (el: Element): boolean => {
    const rect = el.getBoundingClientRect();
    const viewportArea = window.innerWidth * window.innerHeight;
    const elArea = rect.width * rect.height;
    const areaRatio = elArea / (viewportArea || 1);
    const widthRatio = rect.width / window.innerWidth;
    const heightRatio = rect.height / window.innerHeight;

    if (areaRatio > 0.2 || (widthRatio > 0.2 && heightRatio > 0.2)) {
        return true;
    }

    return false;
}

// Try to find the most relevant parent element for CLS shifts
// Sometimes the Web Vitals API gives us a child element, but we want the semantic parent
export const findBestElement = (el: Element): Element => {
    // If element is already large, don't climb.
    try {
        if (isLargeElement(el)) {
            return el;
        }

        if (hasSemanticMeaning(el, true)) {
            return el;
        }

    } catch (error) {
        captureError(error, 'Error in findBestElement initial checks');
    }
    let current = el;
    let bestElement = el;

    // Look up the tree for better semantic elements (up to 3 levels)
    for (let i = 0; i < 3 && current.parentElement; i++) {
        current = current.parentElement;

        if (hasSemanticMeaning(current)) {
            bestElement = current;
            break;
        }

        if (isLargeElement(el)) {
            bestElement = current;
            break;
        }

        // Also prefer elements with compound selectors (multiple classes)
        const classCount = (current.className || '').split(' ').filter(c => c.trim()).length;
        const originalClassCount = (bestElement.className || '').split(' ').filter(c => c.trim()).length;

        if (classCount >= 3 && classCount > originalClassCount) {
            bestElement = current;
        }
    }

    return bestElement;
};